import { useCallback, useEffect, useRef, useState } from "react";
import { FiSave, FiUpload } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { authService } from "../../../services/auth.service";
import { fileAssetUrl } from "../../../utils/fileAssetUrl";
import "../../../styles/users.css";
import "../../../styles/profile.css";

function roleLabel(role) {
  if (role === "BANK_ADMIN") return "Bank Admin";
  if (role === "BANK_PERSON") return "Bank Executive";
  return role || "—";
}

function display(value) {
  const text = String(value || "").trim();
  return text || "—";
}

function bankStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "Active";
  if (s === "pending_payment") return "Pending";
  if (s === "expired") return "Expired";
  if (s === "inactive") return "Blocked";
  return display(status);
}

export default function BankProfile() {
  const { auth, patchUser } = useAuth();
  const photoInputRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [bankAddress, setBankAddress] = useState("");
  const [bankCity, setBankCity] = useState("");
  const [bankState, setBankState] = useState("");
  const [userView, setUserView] = useState(null);
  const [bank, setBank] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const applyUser = useCallback((user) => {
    if (!user) return;
    setName(user.name || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
    setAddress(user.address || "");
    setBankAddress(user.bank?.address || "");
    setBankCity(user.bank?.city || "");
    setBankState(user.bank?.state || "");
    setUserView(user);
    setBank(user.bank || auth?.user?.bank || null);
    setPhotoPreview(fileAssetUrl(user.photoUrl));
  }, [auth?.user?.bank]);

  const loadProfile = useCallback(async () => {
    if (!auth?.token) return;
    try {
      setLoading(true);
      setError("");
      const res = await authService.getProfile(auth.token);
      applyUser(res?.data?.user || res?.user);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load profile");
      applyUser(auth?.user);
    } finally {
      setLoading(false);
    }
  }, [auth?.token, auth?.user, applyUser]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSave = async (e) => {
    e.preventDefault();
    const nextName = name.trim();
    const nextEmail = email.trim().toLowerCase();
    if (nextName.length < 2) {
      setError("Full name is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setError("Enter a valid email.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        name: nextName,
        email: nextEmail,
        phone: phone.trim(),
        address: address.trim(),
      };
      if ((userView?.role || auth?.user?.role) === "BANK_ADMIN") {
        payload.bankAddress = bankAddress.trim();
        payload.bankCity = bankCity.trim();
        payload.bankState = bankState.trim();
      }
      const res = await authService.updateProfile(payload, auth.token);
      const user = res?.data?.user || res?.user;
      if (user) {
        applyUser(user);
        patchUser({
          ...user,
          id: user.id || user._id,
          bank: user.bank || auth?.user?.bank,
        });
      }
      setSuccess("Profile updated.");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    setError("");
    setSuccess("");
    try {
      const res = await authService.uploadProfilePhoto(file, auth.token);
      const user = res?.data?.user || res?.user;
      if (user) {
        applyUser(user);
        patchUser({
          ...user,
          id: user.id || user._id,
          bank: user.bank || auth?.user?.bank,
        });
      }
      setSuccess("Photo updated.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const isBankAdmin = (userView?.role || auth?.user?.role) === "BANK_ADMIN";
  const bankView = bank || {};

  return (
    <div className="page bank-profile-page">
      <form className="bank-profile-page__form" onSubmit={handleSave}>
        <header className="bank-profile-page__header">
          <h2>Profile</h2>
        </header>
        <section className="create-company-section" aria-label="Photo">
          <div className="create-company-upload-slot profile-page__photo-slot">
            <span className="create-company-upload-slot__label">Your photo</span>
            <button
              type="button"
              className="create-company-upload-slot__picker"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto || loading || saving}
            >
              <span className="create-company-upload-preview profile-photo-preview--user" aria-hidden>
                {photoPreview ? (
                  <img src={photoPreview} alt="" />
                ) : (
                  <span className="create-company-upload-preview__placeholder">
                    {(name || "?").charAt(0).toUpperCase()}
                  </span>
                )}
              </span>
              <span className="create-company-upload-slot__action">
                <FiUpload aria-hidden />
                {uploadingPhoto ? "Uploading…" : photoPreview ? "Change photo" : "Upload photo"}
              </span>
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhoto}
            />
          </div>
        </section>

        <section className="create-company-section">
          <header className="create-company-section__head">
            <h3>Personal details</h3>
            <p className="muted">
              {isBankAdmin
                ? "You can edit full name, email, mobile, address, bank address, city, and state."
                : "You can edit full name, email, mobile, and address only."}
            </p>
          </header>
          <div className="form-grid two-column">
            <div className="form-group">
              <label htmlFor="bankProfileName">Full name *</label>
              <input
                id="bankProfileName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading || saving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="bankProfileEmail">Email *</label>
              <input
                id="bankProfileEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || saving}
              />
            </div>
            <div className="form-group full-width">
              <label htmlFor="bankProfileAddress">Address</label>
              <input
                id="bankProfileAddress"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={loading || saving}
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <input
                value={roleLabel(userView?.role || auth?.user?.role)}
                readOnly
                className="input-readonly"
              />
            </div>
            <div className="form-group">
              <label htmlFor="bankProfilePhone">Mobile</label>
              <input
                id="bankProfilePhone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading || saving}
              />
            </div>
            <div className="form-group">
              <label>Designation</label>
              <input value={display(userView?.post)} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Employee number</label>
              <input
                value={display(userView?.employeeNumber)}
                readOnly
                className="input-readonly"
              />
            </div>
          </div>
        </section>

        {isBankAdmin ? (
        <section className="create-company-section">
          <header className="create-company-section__head">
            <h3>Bank / NBFC details</h3>
          </header>
          <div className="form-grid two-column">
            <div className="form-group">
              <label>Bank / NBFC name</label>
              <input value={display(bankView.bankName)} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Bank code</label>
              <input value={display(bankView.bankCode)} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Bank email</label>
              <input value={display(bankView.email)} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Bank phone</label>
              <input value={display(bankView.phone)} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>Branch</label>
              <input
                value={display(bankView.branchName || userView?.branchName)}
                readOnly
                className="input-readonly"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <input
                value={bankStatusLabel(bankView.status)}
                readOnly
                className="input-readonly"
              />
            </div>
            <div className="form-group full-width">
              <label htmlFor="bankOrgAddress">Bank address</label>
              <input
                id="bankOrgAddress"
                value={bankAddress}
                onChange={(e) => setBankAddress(e.target.value)}
                disabled={loading || saving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="bankOrgCity">City</label>
              <input
                id="bankOrgCity"
                value={bankCity}
                onChange={(e) => setBankCity(e.target.value)}
                disabled={loading || saving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="bankOrgState">State</label>
              <input
                id="bankOrgState"
                value={bankState}
                onChange={(e) => setBankState(e.target.value)}
                disabled={loading || saving}
              />
            </div>
            <div className="form-group">
              <label>GST number</label>
              <input value={display(bankView.gstNumber)} readOnly className="input-readonly" />
            </div>
            <div className="form-group">
              <label>PAN number</label>
              <input value={display(bankView.panNumber)} readOnly className="input-readonly" />
            </div>
          </div>
        </section>
        ) : null}

        <div className="create-company-actions profile-page__actions">
          <button type="submit" className="primary-page-btn" disabled={loading || saving}>
            <FiSave aria-hidden />
            {saving ? "Saving…" : "Save profile"}
          </button>
          {success ? <p className="cfm-status profile-page__save-status">{success}</p> : null}
          {error ? <p className="error-text profile-page__save-status">{error}</p> : null}
        </div>
      </form>
    </div>
  );
}
